import React, { useEffect, useState } from "react";
import { message } from "antd";

import { controller as rootController } from "../../controller";
import PolicyCompletionModal from "../TreatmentPlans/component/shared/PolicyCompletionModal";

/**
 * The Data Entry chip's destination: fill in the critical fields a verification
 * needs on ONE policy.
 *
 * Reuses PolicyCompletionModal rather than the full policy form. That modal is
 * already the shared "complete this before we run it" gate for the request flow
 * and the Instant-AI flow, which means it shows exactly the three critical
 * fields, offers the member-DOB suggestion, and — importantly — saves through
 * savePolicyDrafts, which PATCHes only fields the user actually changed.
 *
 * That last part is not a nicety. UpdateInsurancePolicyView appends every field
 * present in the request body to the policy's `manually_edited_fields`, which
 * permanently locks it against future PMS syncs. Sending the whole form would
 * freeze a dozen PMS-owned values every time someone typed one member id.
 *
 * The task row only carries a policy id, so the policy itself is fetched here.
 */
export default function FTCDataEntryModal({ patientId, policyId, onClose, onSaved }) {
  const [policy, setPolicy] = useState(undefined);

  useEffect(
    function () {
      let cancelled = false;
      (async function () {
        try {
          const res = await rootController.getPatientPolicies(patientId);
          if (cancelled) return;
          const list = (res && res.status < 250 && Array.isArray(res.json) && res.json) || [];
          const found = list.find(function (p) { return p.id === policyId; });
          if (found) {
            setPolicy(found);
          } else {
            // Deleted or reassigned between the queue load and the click.
            message.info("That policy is no longer on this patient.");
            onClose();
          }
        } catch (e) {
          console.error(e);
          if (!cancelled) {
            message.error("Couldn't load the policy.");
            onClose();
          }
        }
      })();
      return function () { cancelled = true; };
    },
    [patientId, policyId],
  );

  if (!policy) return null;

  return (
    <PolicyCompletionModal
      open
      policy={policy}
      patientId={patientId}
      title="Complete policy details"
      confirmLabel="Save"
      onCancel={onClose}
      onCompleted={function () {
        message.success("Policy details saved");
        onSaved();
      }}
    />
  );
}
